import { NoopScrollStrategy } from "@angular/cdk/overlay";
import { ComponentType } from "@angular/cdk/portal";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from "@angular/material/sort";
import { TranslateService } from '@ngx-translate/core';
import * as moment from "moment";
import { Observable } from "rxjs";
import { ConfirmDialogComponent } from "../../components/confirm-dialog/confirm-dialog.component";
import { ConfirmDialogCustomButton, ConfirmDialogInput } from "../../components/confirm-dialog/confirm-dialog.models";
import { ImageDialogComponent } from "../../components/image-dialog/image-dialog.component";
import { DatatableParams, Language } from "./pcode.models";

@Injectable()
export class PCodeService
{
	// constructor
	constructor
		(
			private http: HttpClient,
			@Inject('BASE_URL') private baseUrl: string,
			private translateService: TranslateService,
			private snackBar: MatSnackBar,
			private dialog: MatDialog
		)
	{

	}

	// notify
	notify(type: string, message?: string)
	{
		this.snackBar.open(message, this.translateService.instant('Commons.Actions.Close'), {
			panelClass: [`snackbar--${type}`]
		});
	}

	// confirm
	confirm(title?: string, text?: string, okButtonText?: string, cancelButtonText?: string, icon?: string, notesEnabled?: boolean, notesMandatory?: boolean, customButtons?: ConfirmDialogCustomButton[])
	{
		return this.openAppDialog(ConfirmDialogComponent,
			{
				icon: icon || 'delete_outline',
				title: title || this.translateService.instant('Commons.Alerts.ConfirmDialog.Title'),
				text: text || this.translateService.instant('Commons.Alerts.ConfirmDialog.Text'),
				okButtonText: okButtonText || this.translateService.instant('Commons.Alerts.ConfirmDialog.OkButton'),
				cancelButtonText: cancelButtonText || this.translateService.instant('Commons.Alerts.ConfirmDialog.CancelButton'),
				notesEnabled: notesEnabled || false,
				notesMandatory: notesMandatory || false,
				additionalButtons: customButtons || []
			} as ConfirmDialogInput)
			.afterClosed();
	}

	// open app dialog
	openAppDialog<T>(component: ComponentType<T>, data: any = null): MatDialogRef<T, any>
	{
		return this.dialog.open(component,
			{
				width: '500px',
				backdropClass: 'light-dialog-overlay-wrapper',
				panelClass: 'pcode-dialog-panel',
				disableClose: false,
				autoFocus: false,
				position: { top: '50px' },
				scrollStrategy: new NoopScrollStrategy(),
				data: data
			});
	}

	// image dialog
	openImageDialog(imageData: string, fullScreen = false)
	{
		return this.dialog.open(ImageDialogComponent,
			{
				maxWidth: '85vw',
				maxHeight: '85vh',
				width: fullScreen ? '80vw' : '800px',
				backdropClass: 'light-dialog-overlay-wrapper',
				panelClass: ['pcode-dialog-panel', 'image-dialog-panel'],
				disableClose: false,
				autoFocus: false,
				position: { top: '50px' },
				scrollStrategy: new NoopScrollStrategy(),
				data: imageData
			});
	}

	// get languages
	getLanguages(): Observable<Language[]>
	{
		return this.http.get<Language[]>(`${this.baseUrl}api/shared/shared/getLanguages`);
	}

	// get query params from object
	getQueryFromObject(sourceObject): URLSearchParams
	{
		const queryParams = new URLSearchParams();
		for (const key in sourceObject)
		{
			if (sourceObject[key] !== null)
			{
				if (Array.isArray(sourceObject[key]))
				{
					for (const index in sourceObject[key])
					{
						queryParams.append(key, sourceObject[key][index]);
					}
				}
				else
				{
					queryParams.set(key, sourceObject[key]);
				}
			}
		}

		return queryParams;
	}

	// get datatable params from paginator/sort
	getDatatableParams(paginator: MatPaginator, sort: MatSort)
	{
		const datatableParams: DatatableParams =
		{
			sort: sort.active,
			order: sort.direction,
			page: paginator.pageIndex,
			pageSize: paginator.pageSize
		};

		return datatableParams;
	}

	// get formatted duration
	getFormattedDuration(startDate: Date, endDate: Date): string
	{
		var ret = '';

		if (startDate && endDate)
		{
			startDate = new Date(startDate);
			endDate = new Date(endDate);

			var years: number;
			var months: number;

			var totalMonths: number;
			totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12;
			totalMonths -= startDate.getMonth();
			totalMonths += endDate.getMonth();
			totalMonths = totalMonths < 0 ? 0 : totalMonths;

			years = Math.floor(totalMonths / 12);
			months = totalMonths % 12;

			if (years > 0)
				ret += years + ' anni ';
			if (months > 0)
				ret += months + ' mesi ';
		}

		return ret.trim();
	}

	// get formatted bytes
	getFormattedBytes(bytes: number): string
	{
		if (bytes && bytes > 0)
		{
			const thresh = 1024;
			const decimals = 1;

			if (bytes < thresh)
			{
				return bytes + ' B';
			}

			const units = ['KB', 'MB', 'GB', 'TB'];
			let u = -1;
			const r = 10 ** decimals;

			do
			{
				bytes /= thresh;
				++u;
			} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

			return bytes.toFixed(decimals) + ' ' + units[u];
		}

		return '';
	}

	// build formData
	private buildFormData(formData: FormData, data: any, parentKey?: string): void
	{
		if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(Array.isArray(data)))
		{
			Object.keys(data).forEach(key =>
				this.buildFormData(formData, data[key], parentKey ? `${parentKey}.${key}` : key)
			);
		}
		else if (Array.isArray(data))
		{
			for (var i = 0; i < data.length; i++)
				this.buildFormData(formData, data[i], parentKey ? `${parentKey}[${i}]` : `[${i}]`);
		}
		else
		{
			if (data !== null && data !== undefined && data !== '')
				formData.append(parentKey, (data instanceof Date) ? moment(new Date(data)).format('YYYY-MM-DD hh:mm:ss') : data);
		}
	}
	// object to formData
	objectToFormData(data: any): FormData
	{
		const formData = new FormData();

		this.buildFormData(formData, data);

		return formData;
	}
}